// How the bottom row shares its room (Features 4, Tidy part 2): getting started and picking back up on the left (the
// canvas's corner stack, or the card a board without a canvas shows there), the command bar in the middle, zoom on the
// right. The command bar measures the row, on every board kind, and asks this where things go:
// - a pill in the row: the bar at rest keeps clear of it, and when the bar would get less than its least the pill
//   folds to its icon and count, then the bar shows its search icon alone;
// - a card in the row (the checklist open, While you were away, What's new): in a canvas's corner, when it would leave
//   the bar less than its least, the card rises above the row; where the board's own panels fill the left (a board
//   without a canvas) it rises only when the bar would not have even its icon's room, and the bar is its icon (68px up: over zoom and a gap, chrome.css), as toasts rise above zoom, and the
//   bar has the row;
// - whatever is on the left, the bar never passes zoom or the chat: with not even its icon's room, it waits out of
//   sight (Ctrl or ⌘ K, or /, still opens it); and a card is never wider than the room left of the chat.
// Measured on a resize, when the chat opens, shuts or widens, and when the left's cards come and go; never on a timer.

import { useLayoutEffect } from "react";

const PAD = 12;
const BAR = 300;
const BAR_LEAST = 200;
const ICON = 38;
/** about the width of a pill folded to its icon and count, until it is measured folded */
const FOLDED = 80;

/**
 * Where things go in the bottom row. vw: the window; chatW: the chat's width when open, else 0; zoomW: zoom's island, 0
 * where the board has none; left0: where the left's stack starts; empty: nothing in it; full: its bottom pill's whole
 * width; now: the bottom one's width as it shows; tight: the pill folded now; card: the bottom one is a card, not a pill;
 * panels: the board's own panels fill the left column above the row (a board without a canvas), so a card rises late.
 */
export function bottomRow(a: { vw: number; chatW: number; zoomW: number; left0: number; empty?: boolean; full: number; now: number; tight: boolean; card: boolean; panels?: boolean }) {
  // the middle's right edge: left of zoom, which sits left of the chat when it is open
  const chatLeft = a.chatW ? a.vw - 14 - a.chatW : a.vw;
  const right = a.vw - 14 - (a.chatW ? a.chatW + PAD : 0) - (a.zoomW ? a.zoomW + PAD : 0);
  // a card that would leave the bar less than its least (beside the board's own panels, less than its icon) rises
  const lift = !a.empty && a.card && a.left0 + a.now + PAD + (a.panels ? ICON : BAR_LEAST) > right;
  const inRow = !a.empty && !lift;
  const fits = !inRow || a.card || a.left0 + a.full + PAD + BAR_LEAST <= right;
  // the bottom one's width in the row as it will show: a card's, the whole pill's, or the folded pill's (measured once folded)
  const w = !inRow ? 0 : a.card ? a.now : fits ? a.full : a.tight ? a.now : FOLDED;
  const left = inRow ? a.left0 + w + PAD : a.left0;
  const room = right - left;
  const hide = room < ICON;
  // an even width, so its centre is a whole pixel and its edges stay inside the room
  const barW = room >= BAR_LEAST ? Math.min(BAR, Math.floor(room / 2) * 2) : ICON;
  // its centre: the middle of the board's room as before, moved only as far as the left and the right need
  const centre = (a.vw - (a.chatW ? a.chatW + 26 : 0)) / 2;
  const x = hide ? centre : Math.min(Math.max(centre, left + barW / 2), right - barW / 2);
  return { folded: inRow && !fits, barIcon: !hide && room < BAR_LEAST, hide, lift, x: Math.round(x), w: Math.round(barW), max: Math.max(0, Math.round(chatLeft - PAD - a.left0)) };
}

/** The left's stack: the canvas's corner, else the card a board without a canvas shows bottom left (src/help). */
const leftOf = () => document.querySelector<HTMLElement>(".board-root > .corner-stack") ?? document.querySelector<HTMLElement>(".help-notes");

const CLASSES = ["has-corner", "corner-tight", "cmd-tight", "cmd-hide", "corner-lift"];
const VARS = ["--cmd-x", "--cmd-w", "--corner-max", "--notes-left"];

/** Measures the bottom row and says where each thing goes, through classes and properties on the body. */
export function useBottomRow() {
  useLayoutEffect(() => {
    const body = document.body;
    let frame = 0;
    let full = 0;
    let watched: HTMLElement | null = null;
    const put = (k: string, v: string) => body.style.getPropertyValue(k) !== v && body.style.setProperty(k, v);
    const clear = () => {
      // toggled, never removed outright: a class list written with no change is still a change to what this watches
      for (const c of CLASSES) body.classList.toggle(c, false);
      for (const v of VARS) if (body.style.getPropertyValue(v)) body.style.removeProperty(v);
    };
    const measure = () => {
      frame = 0;
      // a phone has its own bar at the foot and no bottom row
      if (body.classList.contains("is-phone")) return clear();
      const el = watched;
      // a board without a canvas shows its card bottom left: beside its own rail of tools there, never on it, as the
      // canvas's corner sits beside the canvas's rail
      if (el && !el.classList.contains("corner-stack")) {
        const rail = document.querySelector<HTMLElement>(".board-root .rail.island")?.getBoundingClientRect();
        put("--notes-left", `${rail && rail.width && rail.left < 70 ? Math.round(rail.right + 4) : 14}px`);
      }
      const shown = el ? Array.from(el.children).filter((k) => (k as HTMLElement).getBoundingClientRect().width > 0) : [];
      const last = (shown.at(-1) as HTMLElement | undefined) ?? null;
      const tight = body.classList.contains("corner-tight");
      const card = !!last && !last.classList.contains("corner-pill");
      const now = last?.getBoundingClientRect().width ?? 0;
      // the pill's whole width, kept from when it last showed whole
      if (last && !tight && !card) full = now;
      const row = bottomRow({
        vw: window.innerWidth,
        chatW: body.classList.contains("chat-open") ? parseFloat(getComputedStyle(body).getPropertyValue("--chat-w")) || 384 : 0,
        zoomW: document.querySelector<HTMLElement>(".board-root > .zoom")?.getBoundingClientRect().width ?? 0,
        left0: el?.getBoundingClientRect().left ?? 14,
        empty: !last,
        full,
        now,
        tight,
        card,
        panels: !!el && !el.classList.contains("corner-stack"),
      });
      body.classList.toggle("has-corner", true);
      body.classList.toggle("corner-tight", row.folded);
      body.classList.toggle("corner-lift", row.lift);
      body.classList.toggle("cmd-tight", row.barIcon);
      body.classList.toggle("cmd-hide", row.hide);
      // written only when it changes: the body's own changes are what this watches
      put("--cmd-x", `${row.x}px`);
      put("--cmd-w", `${row.w}px`);
      put("--corner-max", `${row.max}px`);
    };
    const soon = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const sized = new ResizeObserver(soon);
    const kids = new MutationObserver(() => {
      watch();
      soon();
    });
    // the left's stack, and each card or pill in it: found again when the board's kind changes
    const watch = () => {
      const el = leftOf();
      if (el !== watched) {
        sized.disconnect();
        kids.disconnect();
        watched = el;
        if (el) kids.observe(el, { childList: true });
      }
      if (el) {
        sized.observe(el);
        for (const k of Array.from(el.children)) sized.observe(k);
      }
    };
    // the chat opening, shutting or widening with History; zoom or the left's stack coming and going with the board
    const attrs = new MutationObserver(soon);
    attrs.observe(body, { attributes: true, attributeFilter: ["class", "style"] });
    const root = document.querySelector(".board-root");
    const top = new MutationObserver(() => {
      watch();
      soon();
    });
    if (root) top.observe(root, { childList: true });
    window.addEventListener("resize", soon);
    watch();
    soon();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("resize", soon);
      attrs.disconnect();
      top.disconnect();
      sized.disconnect();
      kids.disconnect();
      clear();
    };
  }, []);
}
